import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Logo } from "../../components/common/Logo";
import { LoginModal } from "../auth/components/LoginModal";
import { RegisterModal } from "../auth/components/RegisterModal";

const PrivacyPolicyPage = () => {
    const { t } = useTranslation("common");
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const activeModal = searchParams.get("modal");
    const openLogin = () => setSearchParams({ modal: "login" });
    const openRegister = () => setSearchParams({ modal: "register" });
    const closeModal = () => setSearchParams({});

    const sections = [
        { title: "legal.privacy.sections.intro.title", content: "legal.privacy.sections.intro.content" },
        { title: "legal.privacy.sections.collection.title", content: "legal.privacy.sections.collection.content" },
        { title: "legal.privacy.sections.legalBasis.title", content: "legal.privacy.sections.legalBasis.content" },
        { title: "legal.privacy.sections.usage.title", content: "legal.privacy.sections.usage.content" },
        { title: "legal.privacy.sections.sharing.title", content: "legal.privacy.sections.sharing.content" },
        { title: "legal.privacy.sections.storage.title", content: "legal.privacy.sections.storage.content" },
        { title: "legal.privacy.sections.retention.title", content: "legal.privacy.sections.retention.content" },
        { title: "legal.privacy.sections.cookies.title", content: "legal.privacy.sections.cookies.content" },
        { title: "legal.privacy.sections.rights.title", content: "legal.privacy.sections.rights.content" },
        { title: "legal.privacy.sections.international.title", content: "legal.privacy.sections.international.content" },
        { title: "legal.privacy.sections.children.title", content: "legal.privacy.sections.children.content" },
        { title: "legal.privacy.sections.changes.title", content: "legal.privacy.sections.changes.content" },
        { title: "legal.privacy.sections.contact.title", content: "legal.privacy.sections.contact.content" },
        { title: "legal.privacy.sections.dataDeletion.title", content: "legal.privacy.sections.dataDeletion.content" },
    ];

    return (
        <div className="min-h-screen bg-background-page font-sans selection:bg-accent-primary/20 selection:text-text-primary">
            <LoginModal isOpen={activeModal === "login"} onClose={closeModal} />
            <RegisterModal isOpen={activeModal === "register"} onClose={closeModal} />

            {/* Navigation */}
            <nav className="fixed top-0 inset-x-0 z-50 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md border-b border-neutral-200 dark:border-neutral-800">
                <div className="container mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate("/")}>
                        <Logo className="w-6 h-6" />
                        <span className="text-xl font-medium font-display text-text-primary tracking-tight">
                            {t("brandName")}
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={openLogin}
                            className="text-sm font-medium text-text-primary dark:text-neutral-200 hover:text-accent-primary transition-colors hidden sm:block"
                        >
                            {t("auth.login")}
                        </button>
                        <button
                            onClick={openRegister}
                            className="bg-text-primary dark:bg-white text-white dark:text-neutral-900 px-5 py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition-all shadow-sm hover:shadow-md"
                        >
                            {t("auth.signUp")}
                        </button>
                    </div>
                </div>
            </nav>

            <main className="pt-32 pb-24">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-3xl mx-auto">
                        <h1 className="text-4xl sm:text-5xl font-bold font-display text-text-primary mb-4 tracking-tight">
                            {t("legal.privacy.title")}
                        </h1>
                        <p className="text-neutral-500 mb-12">
                            {t("legal.lastUpdated")}: {new Date().toLocaleDateString()}
                        </p>

                        <div className="space-y-10">
                            {sections.map((section, index) => (
                                <section key={index} className="prose prose-neutral max-w-none">
                                    <h2 className="text-2xl font-bold text-text-primary dark:text-neutral-100 mb-4">
                                        {t(section.title)}
                                    </h2>
                                    <div className="text-neutral-600 dark:text-neutral-300 leading-relaxed whitespace-pre-line">
                                        {t(section.content)}
                                    </div>
                                </section>
                            ))}
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 py-12">
                <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center text-sm text-neutral-500 dark:text-neutral-400">
                    <div className="flex items-center gap-2.5 mb-4 md:mb-0 cursor-pointer" onClick={() => navigate("/")}>
                        <Logo className="w-6 h-6" />
                        <span className="text-xl font-medium font-display text-text-primary tracking-tight">
                            {t("brandName")}
                        </span>
                    </div>
                    <div className="flex gap-6 mb-4 md:mb-0">
                        <button onClick={() => navigate("/terms")} className="hover:text-text-primary transition-colors">
                            {t("legal.tos.title")}
                        </button>
                    </div>
                    <div>{t("landing.copyright")}</div>
                </div>
            </footer>
        </div>
    );
};

export default PrivacyPolicyPage;
